import React, { useState, useEffect, useMemo } from 'react';
import { Select, Spin } from 'antd';
import { catalogService } from '../../services/api';
import { debounce } from 'lodash';

const { Option } = Select;

const SATCatalogSearch = ({ 
  type, // 'products' o 'units'
  value, 
  onChange, 
  placeholder,
  disabled = false,
  style = {},
  ...props 
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Función de búsqueda con debounce
  const debouncedSearch = useMemo(
    () => debounce(async (searchText) => {
      if (!searchText || searchText.length < 2) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        let response;
        if (type === 'products') {
          response = await catalogService.searchProducts(searchText, 20);
        } else if (type === 'units') {
          response = await catalogService.searchUnits(searchText, 20);
        }

        if (response && response.data) {
          const formattedOptions = response.data.map(item => ({
            value: item.id,
            label: `${item.id} - ${item.name}`,
            descripcion: item.name
          }));
          setOptions(formattedOptions);
        }
      } catch (error) {
        console.error('Error searching catalog:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [type]
  );

  // Cargar opciones iniciales al montar el componente
  useEffect(() => {
    const loadInitialOptions = async () => {
      setLoading(true);
      try {
        let response;
        if (type === 'products') {
          response = await catalogService.searchProducts('', 10);
        } else if (type === 'units') {
          response = await catalogService.searchUnits('', 10);
        }

        if (response && response.data) {
          const formattedOptions = response.data.map(item => ({
            value: item.id,
            label: `${item.id} - ${item.name}`,
            descripcion: item.name
          }));
          setOptions(formattedOptions);
        }
      } catch (error) {
        console.error('Error loading initial options:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialOptions();
  }, [type]);

  // Cargar opción del valor seleccionado si no está en la lista
  useEffect(() => {
    const loadSelectedOption = async () => {
      if (!value) return;
      
      // Si ya está en las opciones, no hacer nada
      if (options.some(opt => opt.value === value)) return;

      try {
        let response;
        if (type === 'products') {
          response = await catalogService.getProduct(value);
        } else if (type === 'units') {
          response = await catalogService.getUnit(value);
        }

        if (response && response.data) {
          const item = response.data;
          const newOption = {
            value: item.id,
            label: `${item.id} - ${item.name}`,
            descripcion: item.name
          };
          
          setOptions(prev => {
            if (prev.some(opt => opt.value === newOption.value)) return prev;
            return [newOption, ...prev];
          });
        }
      } catch (error) {
        console.error('Error loading selected option:', error);
      }
    };

    loadSelectedOption();
  }, [value, type]);

  // Limpiar debounce al desmontar
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = (searchText) => {
    setSearchValue(searchText);
    debouncedSearch(searchText);
  };

  const handleChange = (selectedValue, option) => {
    if (onChange) {
      onChange(selectedValue, option);
    }
  };

  const handleClear = () => {
    setSearchValue('');
    setOptions([]);
    if (onChange) {
      onChange(undefined);
    }
  };

  return (
    <Select
      showSearch
      value={value}
      placeholder={placeholder}
      style={{ width: '100%', ...style }}
      filterOption={false}
      onSearch={handleSearch}
      onChange={handleChange}
      onClear={handleClear}
      allowClear
      disabled={disabled}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : 'No se encontraron resultados'}
      {...props}
    >
      {options.map(option => (
        <Option key={option.value} value={option.value} title={option.descripcion}>
          {option.label}
        </Option>
      ))}
    </Select>
  );
};

export default SATCatalogSearch;